import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiEdit3,
  FiImage,
  FiHelpCircle,
  FiFileText,
  FiUsers,
  FiBarChart2,
  FiAward,
  FiPlus,
} from "react-icons/fi";
import CreatePostModal from "./CreatePostModal";
import "./CreatePostBox.css";
import { useUserContext } from "../../../context/UserContext";
import {
  getProfilePhoto,
  getCachedPhoto,
} from "../../../utils/profilePhotoCache";
import { getAuthToken } from "../../../utils/apiUtils";

// Post type configuration with icons and colors
const postTypes = [
  {
    id: "text",
    label: "Text",
    icon: FiEdit3,
    color: "indigo",
    desktopOnly: true,
  },
  {
    id: "photo",
    label: "Photo",
    icon: FiImage,
    color: "blue",
    desktopOnly: true,
  },
  {
    id: "question",
    label: "Question",
    icon: FiHelpCircle,
    color: "amber",
    desktopOnly: true,
  },
  {
    id: "research",
    label: "Research",
    icon: FiFileText,
    color: "green",
    desktopOnly: false,
  },
  {
    id: "collaboration",
    label: "Collab",
    icon: FiUsers,
    color: "cyan",
    desktopOnly: false,
  },
  {
    id: "poll",
    label: "Poll",
    icon: FiBarChart2,
    color: "pink",
    desktopOnly: false,
  },
  {
    id: "milestone",
    label: "Milestone",
    icon: FiAward,
    color: "amber",
    desktopOnly: false,
  },
];

// Mobile-hidden items for "More" dropdown
const moreItems = postTypes.filter((type) => type.desktopOnly);

const CreatePostBox = ({ onPostCreated }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPostType, setSelectedPostType] = useState("text");
  const [userPhotoUrl, setUserPhotoUrl] = useState(getCachedPhoto() || null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const navigate = useNavigate();
  const { user } = useUserContext();

  // Get user initials for fallback
  const getUserInitials = () => {
    if (!user) return "U";
    const name = user.name || user.username || user.email || "";
    const nameParts = name.trim().split(/\s+/);

    if (nameParts.length >= 2) {
      return (
        nameParts[0][0] + nameParts[nameParts.length - 1][0]
      ).toUpperCase();
    } else if (nameParts[0] && nameParts[0].length >= 2) {
      return nameParts[0].substring(0, 2).toUpperCase();
    } else if (nameParts[0] && nameParts[0].length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    return "U";
  };

  // Load profile photo with optimized caching
  useEffect(() => {
    const loadPhoto = async () => {
      const token = getAuthToken();
      if (token && user) {
        const photo = await getProfilePhoto(token, user.photoUpdatedAt);
        setUserPhotoUrl(photo || null);
      }
    };
    loadPhoto();
  }, [user?.profilePhotoUrl, user?.photoUpdatedAt]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showMoreMenu && !e.target.closest(".cbx-more-wrapper")) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showMoreMenu]);

  const handleOpenModal = (postType = "text") => {
    setSelectedPostType(postType);
    setShowModal(true);
    setShowMoreMenu(false);
  };

  return (
    <>
      <motion.div
        className="cbx-create-post"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Input Section */}
        <div className="cbx-create-input">
          <div className="cbx-create-avatar">
            <div className="cbx-create-avatar-wrapper">
              {userPhotoUrl ? (
                <img
                  src={userPhotoUrl}
                  alt="Your profile"
                  className="cbx-create-avatar-img"
                />
              ) : (
                <div className="cbx-create-avatar-initials">
                  {getUserInitials()}
                </div>
              )}
            </div>
          </div>
          <motion.button
            className="cbx-create-trigger"
            onClick={() => handleOpenModal("text")}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <span>Share your research insights...</span>
          </motion.button>
        </div>

        {/* Divider */}
        <div className="cbx-create-divider" />

        {/* Post Type Options */}
        <div className="cbx-post-types">
          {/* Desktop: Show all */}
          {postTypes.map((type) => {
            const Icon = type.icon;
            return (
              <motion.button
                key={type.id}
                className={`cbx-type-btn cbx-type-btn--${type.color} ${
                  type.desktopOnly ? "cbx-desktop-only" : ""
                }`}
                onClick={() => handleOpenModal(type.id)}
                whileTap={{ scale: 0.95 }}
              >
                <Icon className="cbx-type-icon" />
                <span className="cbx-type-label">{type.label}</span>
              </motion.button>
            );
          })}

          {/* Mobile: More button with dropdown */}
          <div className="cbx-more-wrapper cbx-mobile-only">
            <motion.button
              className="cbx-type-btn cbx-type-btn--more"
              onClick={(e) => {
                e.stopPropagation();
                setShowMoreMenu(!showMoreMenu);
              }}
              whileTap={{ scale: 0.95 }}
            >
              <FiPlus className="cbx-type-icon" />
              <span className="cbx-type-label">More</span>
            </motion.button>

            <AnimatePresence>
              {showMoreMenu && (
                <motion.div
                  className="cbx-more-dropdown"
                  initial={{ opacity: 0, y: -8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  {moreItems.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        className={`cbx-more-item cbx-type-btn--${type.color}`}
                        onClick={() => handleOpenModal(type.id)}
                      >
                        <Icon className="cbx-type-icon" />
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <CreatePostModal
            initialPostType={selectedPostType}
            onClose={() => setShowModal(false)}
            onSuccess={(newPost) => {
              setShowModal(false);
              if (onPostCreated) {
                onPostCreated(newPost);
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default CreatePostBox;
